import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadScript } from '@guestyorg/tokenization-js';
import { RETRY_LEAVES_LIVE_LINE, useChargeErrorCopy } from '../lib/useChargeErrorCopy';

const BESTY_PARTNER_ID = '654a5608898822083ef95a8b';

/**
 * BES-10047: options for the Guesty-hosted card form. The form lives in an iframe we
 * cannot style from outside, but `render()` accepts a theme and a few layout switches.
 *
 * `mobileBreakpoint: 9999` keeps the form single-column at every width. Above the SDK's
 * default 450px it packs Country/State/Zip into one row of 137px dropdowns, which is what
 * made the State picker look broken. Hiding the card logos and PCI footer saves ~108px so
 * more of the form fits above the fold on a phone. Font/colour/radius mirror our inputs.
 */
export const GUESTY_PAY_RENDER_OPTIONS = {
  showSupportedCards: false,
  showPciCompliantLink: false,
  styles: {
    mobileBreakpoint: 9999,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSizeMd: 15,
    borderRadius: 12,
    inputHeight: 48,
    colorText: '#18181b',
    colorPlaceholder: '#a1a1aa',
    colorBorder: '#d4d4d8',
    colorBorderHover: '#3e68c0',
    colorTextError: '#991b1b',
    colorBorderError: '#fca5a5',
  },
} as const;

/**
 * BES-10047: Guesty positions its Country/State list before applying the list's final
 * width. Inside an iframe narrower than its UI kit's 768px "md" breakpoint (always, for us)
 * that leaves the list right-aligned to the field and running off the iframe's right edge
 * by (field width - ~200px). The positioner does recompute on a `resize` event inside the
 * iframe, so while focus is inside the iframe we shrink it by 1px and restore it 400ms later,
 * every 800ms. Width, not height: on iOS a height change does not fire `resize` inside the
 * iframe. Polling, not a focus/blur trigger: the guest fills the card fields inside the
 * iframe first, so focus never crosses back into our document when a list opens, and iOS
 * does not raise the keyboard for the list's programmatic autofocus either. Verified on the
 * iOS 26 simulator (list corrected ~500ms after opening) and in Chromium/WebKit emulation.
 */
export function useGuestyPayDropdownRepositionNudge(iframeId = 'guesty-tokenization-iframe'): void {
  useEffect(() => {
    let restoring = false;
    let restoreTimer: ReturnType<typeof setTimeout> | undefined;
    const interval = setInterval(() => {
      const iframe = document.getElementById(iframeId);
      if (!(iframe instanceof HTMLIFrameElement) || document.activeElement !== iframe || restoring) return;
      restoring = true;
      iframe.style.width = 'calc(100% - 1px)';
      restoreTimer = setTimeout(() => {
        iframe.style.width = '100%';
        restoring = false;
      }, 400);
    }, 800);
    return () => {
      clearInterval(interval);
      if (restoreTimer) clearTimeout(restoreTimer);
      const iframe = document.getElementById(iframeId);
      if (iframe instanceof HTMLIFrameElement) iframe.style.width = '100%';
    };
  }, [iframeId]);
}

/** Prefill for the hosted form. Country/state are ISO codes ("US", "KS"). */
export interface GuestyPayInitialValues {
  firstName?: string;
  lastName?: string;
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

// BES-9535: the block has to outlive the modal, so it rides in sessionStorage. Keyed by
// reservation AND product: an orphan line from one add-on says nothing about whether a
// different add-on can be bought, and blocking the whole stay strands purchases we could
// still take. We store the failure CODE, not a boolean, so a remount can show the guest why
// Pay is down instead of a greyed button with no copy. Storage can throw (Safari private
// mode, embedded webviews) and a modal that will not render is worse than one that forgets a
// block, so every access is guarded.
const retryBlockedKey = (reservationId?: string | null, productKey?: string | null) =>
  reservationId ? `besty:gp-retry-blocked:${reservationId}:${productKey ?? 'default'}` : null;

function readRetryBlock(reservationId?: string | null, productKey?: string | null): string | null {
  const key = retryBlockedKey(reservationId, productKey);
  if (!key) return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRetryBlock(reservationId: string | null | undefined, productKey: string | null | undefined, code: string): void {
  const key = retryBlockedKey(reservationId, productKey);
  if (!key) return;
  try {
    window.sessionStorage.setItem(key, code);
  } catch {
    /* no storage: the in-memory flag still covers this mount */
  }
}

interface GuestyPayCardModalProps {
  paymentProviderId: string;
  listingId?: string | null;
  reservationId?: string | null;
  /** Scopes the terminal-failure block so one add-on's orphan line does not lock the others. */
  productKey?: string;
  amount: number;
  currency: string;
  title?: string;
  subtitle?: string;
  submitLabel?: string;
  /** BES-10047: pre-populates the hosted form so the guest types (and scrolls) less. */
  initialValues?: GuestyPayInitialValues;
  onTokenized: (tokenId: string) => Promise<void>;
  onCancel: () => void;
  // BES-7690: report tokenization/charge failures to the backend telemetry sink.
  onError?: (stage: 'tokenize' | 'purchase', err: unknown) => void;
}

export function GuestyPayCardModal({
  paymentProviderId,
  listingId,
  reservationId,
  productKey,
  amount,
  currency,
  title,
  subtitle,
  submitLabel,
  initialValues,
  onTokenized,
  onCancel,
  onError,
}: GuestyPayCardModalProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const sdkRef = useRef<Awaited<ReturnType<typeof loadScript<'v2'>>> | null>(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  useGuestyPayDropdownRepositionNudge();
  // BES-9535: set when the failure left a folio line posted, which makes another Pay press
  // dangerous rather than merely useless. `guesty_validation` is deliberately NOT here: that
  // path reverses the line, so trying again is safe and the copy invites it.
  //
  // Persisted per reservation, because component state dies with the modal: the guest could
  // hit Cancel, reopen, and post a SECOND line against the one still sitting on the folio,
  // which is the exact behaviour the flag exists to stop. Session-scoped on purpose, so it
  // clears with the tab once ops has had a chance to remove the line.
  const [blockedCode, setBlockedCode] = useState<string | null>(() => readRetryBlock(reservationId, productKey));
  const retryBlocked = blockedCode !== null;
  const chargeErrorCopy = useChargeErrorCopy();
  const blockRetries = useCallback(
    (code: string) => {
      setBlockedCode(code);
      writeRetryBlock(reservationId, productKey, code);
    },
    [reservationId, productKey]
  );
  // Restored blocks arrive with no error state, so seed the copy from the stored code. The
  // guest would otherwise reopen to a live card form with Pay greyed out and no explanation.
  const [cardError, setCardError] = useState<string | null>(() => {
    const stored = readRetryBlock(reservationId, productKey);
    return stored ? chargeErrorCopy(stored) : null;
  });

  useEffect(() => {
    let destroyed = false;
    (async () => {
      try {
        const sdk = await loadScript({ version: 'v2' });
        if (destroyed || !sdk || !containerRef.current) return;
        sdkRef.current = sdk;
        await sdk.render({
          containerId: 'guesty-pay-container',
          providerId: paymentProviderId,
          onStatusChange: (valid: boolean) => setSdkReady(valid),
          ...GUESTY_PAY_RENDER_OPTIONS,
          ...(initialValues ? { initialValues } : {}),
        });
      } catch {
        if (!destroyed) setCardError(t('Failed to load payment form'));
      }
    })();
    return () => {
      destroyed = true;
      try {
        sdkRef.current?.destroy();
      } catch {
        // SDK throws if iframe already removed from DOM
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialValues is a mount-time seed
  }, [paymentProviderId, t]);

  // BES-10047: the overlay is the scroll container, so stop the page underneath from
  // scrolling too (iOS otherwise rubber-bands the page when a swipe lands on the backdrop).
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!sdkRef.current || retryBlocked) return;
    setProcessing(true);
    setCardError(null);
    // BES-7690: split tokenization from the charge so a failure is attributed to
    // the right stage for telemetry (the tokenize stage is otherwise invisible to
    // us: it runs in the browser and never reaches our backend).
    let paymentMethod: { _id?: string } | undefined;
    try {
      const submitPayload = listingId && reservationId
        ? { amount, currency, listingId, reservationId, apiVersion: 'v3' as const, partnerId: BESTY_PARTNER_ID }
        : { amount, currency, apiVersion: 'v2' as const };
      // partnerId is accepted by the SDK runtime but not in the published types
      paymentMethod = await sdkRef.current.submit(
        submitPayload as Parameters<typeof sdkRef.current.submit>[0]
      );
    } catch (err) {
      onError?.('tokenize', err);
      setCardError(err instanceof Error ? err.message : t('Payment failed'));
      setProcessing(false);
      return;
    }
    if (!paymentMethod?._id) {
      onError?.('tokenize', new Error('Card tokenization returned no id'));
      setCardError(t('Card tokenization failed'));
      setProcessing(false);
      return;
    }
    try {
      await onTokenized(paymentMethod._id);
    } catch (err) {
      onError?.('purchase', err);
      const code = (err as { code?: string } | undefined)?.code;
      setCardError(chargeErrorCopy(code, err instanceof Error ? err.message : undefined));
      // BES-9535: these two leave a live folio line behind, so pressing Pay again posts a
      // SECOND one. One damage-waiver reservation collected nine lines exactly this way.
      // Keep the button down; the copy above tells the guest to message us.
      if (code && RETRY_LEAVES_LIVE_LINE.has(code)) blockRetries(code);
    } finally {
      setProcessing(false);
    }
  }, [listingId, reservationId, amount, currency, onTokenized, onError, chargeErrorCopy, retryBlocked, blockRetries, t]);

  return (
    // BES-10047: the overlay itself scrolls. The old modal was a 90vh box with its own
    // inner scroll area around a ~1000px-tall iframe; Guesty draws the State dropdown
    // INSIDE that iframe (260px list + a full-iframe backdrop with touch-action: none), so
    // the list was clipped by our box and no swipe on the iframe could move the box. With
    // one viewport-sized scroller and no nested clipping the list is fully visible. On
    // phones the sheet is full-width with sticky header/footer; from `sm` up it is the
    // same centred card as before, just without the inner scroll.
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 sm:flex sm:justify-center sm:p-6">
      <div
        className="flex min-h-full flex-col bg-white sm:my-auto sm:min-h-0 sm:w-full sm:max-w-lg sm:rounded-2xl"
        style={{ boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}
      >
        {/* Header scrolls away on purpose: Guesty's State/Country list opens UPWARD out of the
            field, so a sticky header would sit on top of the list's search box. */}
        <div className="shrink-0 border-b border-zinc-100 bg-white p-5 sm:rounded-t-2xl">
          <h3 className="text-lg font-bold text-zinc-900">{title || t('Enter Payment Details')}</h3>
          <p className="text-sm text-zinc-500 mt-1">{subtitle || t('Your card will be charged for this purchase.')}</p>
        </div>
        <div className="flex-1 p-5">
          <div id="guesty-pay-container" ref={containerRef} className="min-h-[200px]" />
          {cardError && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-red-800 text-sm">{cardError}</p>
            </div>
          )}
        </div>
        <div
          className="sticky bottom-0 z-10 flex shrink-0 gap-3 border-t border-zinc-100 bg-white p-5 sm:static sm:rounded-b-2xl"
          style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={processing}
            className="flex-1 rounded-xl border border-zinc-300 bg-white px-4 py-3 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          >
            {t('Cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!sdkReady || processing || retryBlocked}
            className="flex-1 rounded-xl bg-[var(--besty-blue,#3b82f6)] px-4 py-3 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {processing ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white mr-2" />
                {t('Processing...')}
              </div>
            ) : (
              submitLabel || t('Pay')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
